import { keyBy } from 'lodash';
import { getAlpha2ByAlpha3 } from 'country-locale-map';
import dot from 'dot-object';

export const remapMetadata = (metadata) => {
  return [
    'basementFinishTypes',
    'commentRegardingTypes',
    'commentServiceTypes',
    'contactInfoOwnerTypes',
    'contactPreferenceTypes',
    'contactTypes',
    'emailTypes',
    'familiarWithAreaTypes',
    'hoaPaymentScheduleTypes',
    'homesaleTypes',
    'houseHeatTypes',
    'hhgMoveTypes',
    'houseInspectionTypes',
    'housePoolTypes',
    'housePropertyTypes',
    'houseRoofStyleTypes',
    'houseSewageTypes',
    'houseSidingTypes',
    'houseStyleTypes',
    'houseWaterSourceTypes',
    'maritalStatuses',
    'moveReasons',
    'moveTypes',
    'petTypes',
    'phoneTypes',
    'preferenceTypes',
    'propertyCreditBalanceTypes',
    'reloPolicyTypes',
    'taxFilingStatuses',
    'solarPanelOwnTypes',
  ].reduce((remappedData, metadataType) => {
    if (metadata[metadataType]) {
      metadata[metadataType] = metadata[metadataType].filter((md) => md.isActive);
    }
    remappedData[metadataType] = keyBy(metadata[metadataType], 'id');
    return remappedData;
  }, {});
};

const parseKey = (key, removePrefix) => {
  const parts = key.split('.').map((part) => part.charAt(0).toLowerCase() + part.substring(1));
  return (parts.length > 1 && parts[0] === removePrefix ? parts.splice(1): parts).join('.');
};

export const remapValidationResults = (response) => {
  // V2 API returns { errors: [...], isValid: boolean }
  // Old API returned array of sections with section.name and section.result.errors
  
  // Handle new V2 API format
  if (response && response.errors && Array.isArray(response.errors)) {
    // Group errors by section
    const groupedBySection = response.errors.reduce((acc, error) => {
      if (!acc[error.section]) {
        acc[error.section] = [];
      }
      acc[error.section].push(error);
      return acc;
    }, {});

    // Transform to the expected format
    return Object.keys(groupedBySection).reduce((results, sectionName) => {
      return {
        ...results,
        [sectionName]: groupedBySection[sectionName].reduce((errors, error) => {
          const key = parseKey(error.propertyName, sectionName);
          return {
            ...errors,
            ...dot.object({
              [key]: error.errorMessage,
            }),
          };
        }, {}),
      };
    }, {});
  }

  // Handle legacy format (if still used anywhere)
  if (Array.isArray(response)) {
    return response.reduce((results, section) => {
      return {
        ...results,
        [section.name]: section.result.errors.reduce((errors, error) => {
          const key = parseKey(error.propertyName, section.name);
          return {
            ...errors,
            ...dot.object({
              [key]: error.errorMessage,
            }),
          };
        }, {}),
      };
    }, {});
  }

  // Return empty object if response format is unrecognized
  return {};
};

export const parseRocketInfo = (intakeRecord, rcInfo) => {
  const mainContact = intakeRecord.contacts[0];
  const secondaryContact = intakeRecord.contacts[1];
  const { departureProperty, phoneNumbers } = intakeRecord;

  const residencyTypes = [{ id: 1, description: 'Own' }, { id: 2, description: 'Rent' }, { id: 3, description: 'Unknown' }];
  let residencyType = null;
  if (intakeRecord.mortgageStatusOfTransferee != null) {
    residencyType = residencyTypes.find((os) => os.id === intakeRecord.mortgageStatusOfTransferee).description;
  }

  let relocationPolicyCode = null;
  if (intakeRecord.relocationPolicyCodeId && intakeRecord.client && intakeRecord.client.relocationPolicyCodes) {
    relocationPolicyCode = intakeRecord.client.relocationPolicyCodes.find((rpc) => rpc.id === intakeRecord.relocationPolicyCodeId)?.policyCode;
  }

  const homePhone = phoneNumbers.find((o) => o.phoneTypeId === 0)?.phoneNumber.replace('+1', '').replace(/\D/g, '');
  let cellPhone = phoneNumbers.find((o) => o.phoneTypeId === 1)?.phoneNumber.replace('+1', '').replace(/\D/g, '');
  // let spouseCellPhone = phoneNumbers.find((o) => o.phoneTypeId === 1 && o.contactInfoOwnerTypeId === 2)?.phoneNumber;
  // const workPhone = phoneNumbers.find((o) => o.phoneTypeId === 2)?.phoneNumber;
  
  // cellPhone = parseInt(cellPhone?.replace('+1', '').replace(/\D/g, ''));
  // spouseCellPhone = parseInt(spouseCellPhone?.replace('+1', '').replace(/\D/g, ''));

  let returnBody = {
    applicants: [
      {
        type: 'primary',
        firstName: mainContact.firstName,
        lastName: mainContact.lastName,
        email: intakeRecord.emailAddresses[0]?.emailAddress,
        phoneNumber: homePhone ? homePhone.toString() : (cellPhone ? cellPhone.toString() : undefined),
        cellPhone : cellPhone ? cellPhone.toString() : undefined,
        currentResidence: {
          residencyBasisTypeIdentifier: residencyType ? residencyType : 'Unknown',
          address: {
            addressLine1: departureProperty.address1,
            city: departureProperty.city,
            state: departureProperty.stateCode,
            zipCode: departureProperty.postalCode,
            country: getAlpha2ByAlpha3(departureProperty.countryCode),
          },
        },
        income: {
          currentEmployment: [{
            employerName: intakeRecord.client.description,
          }],
        },
      }
    ],
    languageCode: intakeRecord.mortgageSpanishSpeaking ? 'spa' : 'eng',
    relocation: {
      authCode: intakeRecord.mtAuthorizationId,
      reloConsultantEmail: rcInfo.email,
      reloPolicyType: relocationPolicyCode ? relocationPolicyCode : '',
      closingCostMethod: 
        intakeRecord.mortgageClosingCostMethod === 1 ? 'Direct Bill' :
        intakeRecord.mortgageClosingCostMethod === 2 ? 'Lump Sum' :
        intakeRecord.mortgageClosingCostMethod === 3 ? 'Reimbursement' :
        intakeRecord.mortgageClosingCostMethod === 4 ? 'None' :
        'Unknown',
    },
    employerName: intakeRecord.client.description,
    comments: intakeRecord.mortgageLeadMessage,
    subjectProperty: {
      desiredLocation: {
        city: intakeRecord.destinationCity,
        state: intakeRecord.destinationStateCode,
      },
    },
  }

  if ((secondaryContact?.firstName && secondaryContact?.firstName.length > 0) && (secondaryContact?.lastName && secondaryContact?.firstName.lastName > 0) && (intakeRecord.mortgageCoApplicantPhone && intakeRecord.mortgageCoApplicantPhone.length > 0)) {
    returnBody.applicants.push({
      type: 'coApplicant',
      firstName: secondaryContact?.firstName ? secondaryContact.firstName : '',
      lastName: secondaryContact?.lastName ? secondaryContact.lastName : '',
      email: intakeRecord.mortgageCoApplicantEmail,
      cellPhone: intakeRecord.mortgageCoApplicantPhone ? intakeRecord.mortgageCoApplicantPhone.replace('+1', '').replace(/\D/g, '') : undefined,
    });
  }

  return returnBody;
};

export const parsePncPermiaInfo = (intakeRecord) => {
  return {
    mtAuthorizationId: intakeRecord.mtAuthorizationId,
    relocationConsultantId: intakeRecord.relocationConsultantId,
    intakeRecordStatusId: intakeRecord.intakeRecordStatusId,
    clientId: intakeRecord.clientId,
    client: {
      id: intakeRecord.client.id,
      description: intakeRecord.client.description,
      moveTrackClientId: intakeRecord.client.moveTrackClientId,
    },
    departureCity: intakeRecord.departureCity,
    departureStateCode: intakeRecord.departureStateCode,
    destinationCity: intakeRecord.destinationCity,
    destinationStateCode: intakeRecord.destinationStateCode,
    relocationPolicyCodeId: intakeRecord.relocationPolicyCodeId,
    newJobStartDate: intakeRecord.newJobStartDate,
    contacts: intakeRecord.contacts,
    phoneNumbers: intakeRecord.phoneNumbers,
    emailAddresses: intakeRecord.emailAddresses,
    departureProperty: {
      isRental: intakeRecord.departureProperty.isRental,
      address1: intakeRecord.departureProperty.address1,
      address2: intakeRecord.departureProperty.address2,
      address3: intakeRecord.departureProperty.address3,
      stateCode: intakeRecord.departureProperty.stateCode,
      city: intakeRecord.departureProperty.city,
      postalCode: intakeRecord.departureProperty.postalCode,
      countryCode: intakeRecord.departureProperty.countryCode,
    },
    id: intakeRecord.id,
    Subsidy: false, // TODO: Add field to intakeRecord
    InternationalCredit: false, // TODO: Add field to intakeRecord
    // directBillComment: 'Yes - typical customary non-recurring buyer costs', // TODO: Add field to intakeRecord
    spanishSpeaking: intakeRecord.mortgageSpanishSpeaking ?? false,
    transfereeStatus: intakeRecord.mortgageStatusOfTransferee ?? 3, // Default to Unkown
    benefitCap: intakeRecord.mortgageBenefitsCap ?? false,
    benefitCapAmount: intakeRecord.mortgageAmountOfCap,
    additionalComment: intakeRecord.mortgageLeadMessage,
    closingCostMethod: intakeRecord.mortgageClosingCostMethod ?? 5, // Default to Unkown
    coApplicantEmail: intakeRecord.mortgageCoApplicantEmail,
    coApplicantPhone: intakeRecord.mortgageCoApplicantPhone,
    relocationType: intakeRecord.mortgageTypeOfRelocation ?? 1, // Default to unkown
    directBillEligible: intakeRecord.mortgageDirectBillEligible ? intakeRecord.mortgageDirectBillEligible.toString() : 'No',
  };
};

export const mortgageLeadsAreValid = (leads, moveType) => {
  if (moveType === 1) {
    if (leads.dynamicVendors.length < leads.staticVendors.length && (leads.reason === null || leads.reason?.length === 0 || (/^\s*$/.test(leads.reason)))) {
      return false;
    } else if (leads.dynamicVendors.length > leads.staticVendors.length)
    {
      if ((leads.closingCostMethod?.length === 0 || leads.typeOfRelocation?.length === 0 || leads.statusOfTransferee?.length === 0 || leads.directBillEligible?.length === 0 || (leads.benefitsCap && leads.amountOfCap?.length === 0)))
      {
        return false; 
      }
      return true;
    } 
    return true;
  } else {
    return true;
  }
};

/**
 * Converts an object's keys from PascalCase to camelCase (recursively)
 */
export const toCamelCaseKeys = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  
  if (Array.isArray(obj)) {
    return obj.map(item => toCamelCaseKeys(item));
  }
  
  return Object.keys(obj).reduce((acc, key) => {
    // Convert first character to lowercase for camelCase
    const camelKey = key.charAt(0).toLowerCase() + key.slice(1);
    // Recursively convert nested objects/arrays
    acc[camelKey] = toCamelCaseKeys(obj[key]);
    return acc;
  }, {});
};

/**
 * Converts an object's keys from camelCase to PascalCase (recursively)
 */
const toPascalCaseKeys = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  
  if (Array.isArray(obj)) {
    return obj.map(item => toPascalCaseKeys(item));
  }
  
  return Object.keys(obj).reduce((acc, key) => {
    // Convert first character to uppercase for PascalCase
    const pascalKey = key.charAt(0).toUpperCase() + key.slice(1);
    // Recursively convert nested objects/arrays
    acc[pascalKey] = toPascalCaseKeys(obj[key]);
    return acc;
  }, {});
};

/**
 * Transforms the V2 API IntakeRecordWithChildren response to the flat structure
 * expected by the frontend (matching the old IntakeRecordDto format)
 */
export const flattenIntakeRecordV2Response = (intakeRecordWithChildren) => {
  if (!intakeRecordWithChildren) {
    return null;
  }

  const {
    intakeRecord,
    contacts,
    pets,
    phones,
    emails,
    notes,
    authorizationComments,
    miscTimelineEntries,
    departureProperty,
    hhgServiceDetail,
    destinationServiceInfo,
    temporaryLivingInfo,
    relocationAllowance,
    homesaleProcess,
    hhgBenefitsTimeline,
    spousalAssistance,
    houseHuntingInfo,
    finalMoveInfo,
    candidateInternInfo,
    globalServiceInfo,
  } = intakeRecordWithChildren;

  // Convert IntakeRecord properties from PascalCase to camelCase
  const camelCaseIntakeRecord = toCamelCaseKeys(intakeRecord);

  // Flatten the nested structure to match the old API format
  return {
    ...camelCaseIntakeRecord,
    contacts: toCamelCaseKeys(contacts) || [],
    pets: toCamelCaseKeys(pets) || [],
    phoneNumbers: toCamelCaseKeys(phones) || [],  // Note: V2 uses 'phones', old API uses 'phoneNumbers'
    emailAddresses: toCamelCaseKeys(emails) || [], // Note: V2 uses 'emails', old API uses 'emailAddresses'
    notes: toCamelCaseKeys(notes) || [],
    authorizationComments: toCamelCaseKeys(authorizationComments) || [],
    miscTimelineEntries: toCamelCaseKeys(miscTimelineEntries) || [],
    departureProperty: toCamelCaseKeys(departureProperty) || {},
    intakeHhgServiceDetail: toCamelCaseKeys(hhgServiceDetail) || null,
    destinationServiceInfo: toCamelCaseKeys(destinationServiceInfo) || null,
    temporaryLivingInfo: toCamelCaseKeys(temporaryLivingInfo) || null,
    relocationAllowance: toCamelCaseKeys(relocationAllowance) || null,
    homesaleProcess: toCamelCaseKeys(homesaleProcess) || null,
    hhgBenefitsTimeline: toCamelCaseKeys(hhgBenefitsTimeline) || null,
    spousalAssistance: toCamelCaseKeys(spousalAssistance) || null,
    houseHunting: toCamelCaseKeys(houseHuntingInfo) || null,
    finalMove: toCamelCaseKeys(finalMoveInfo) || null,
    candidateInternInfo: toCamelCaseKeys(candidateInternInfo) || null,
    globalServiceInfo: toCamelCaseKeys(globalServiceInfo) || null,
  };
};

/**
 * Transforms the flat frontend structure to the V2 API IntakeRecordWithChildren format
 * Uses explicit property picking (whitelist approach) to ensure only valid properties are sent
 */
export const nestIntakeRecordForV2Api = (flatRecord) => {
  if (!flatRecord) {
    return null;
  }

  // Extract child entities from flatRecord (these should NOT be in intakeRecord)
  const childCollections = [
    'contacts', 'pets', 'phoneNumbers', 'emailAddresses', 'phones', 'emails',
    'notes', 'authorizationComments', 'miscTimelineEntries',
    'departureProperty', 'intakeHhgServiceDetail', 'destinationServiceInfo',
    'temporaryLivingInfo', 'relocationAllowance', 'homesaleProcess',
    'hhgBenefitsTimeline', 'spousalAssistance', 'houseHunting',
    'finalMove', 'candidateInternInfo', 'client', 'globalServiceInfo',
    'intakeRecord', // Nested V2 structure
  ];

  // Create intakeRecordOnly by filtering out child collections
  const intakeRecordOnly = Object.keys(flatRecord).reduce((acc, key) => {
    if (!childCollections.includes(key)) {
      acc[key] = flatRecord[key];
    }
    return acc;
  }, {});

  // Handle both property names for phones/emails 
  const phonesList = flatRecord.phoneNumbers || flatRecord.phones || [];
  const emailsList = flatRecord.emailAddresses || flatRecord.emails || [];

  // Create the nested structure expected by V2 API
  // Wrapper properties are camelCase, nested entity properties are PascalCase (matching API GET response format)
  return {
    intakeRecord: toPascalCaseKeys(intakeRecordOnly),
    contacts: toPascalCaseKeys(flatRecord.contacts) || [],
    pets: toPascalCaseKeys(flatRecord.pets) || [],
    phones: toPascalCaseKeys(phonesList) || [],
    emails: toPascalCaseKeys(emailsList) || [],
    notes: toPascalCaseKeys(flatRecord.notes) || [],
    authorizationComments: toPascalCaseKeys(flatRecord.authorizationComments) || [],
    miscTimelineEntries: toPascalCaseKeys(flatRecord.miscTimelineEntries) || [],
    departureProperty: toPascalCaseKeys(flatRecord.departureProperty) || null,
    hhgServiceDetail: toPascalCaseKeys(flatRecord.intakeHhgServiceDetail) || null,
    destinationServiceInfo: toPascalCaseKeys(flatRecord.destinationServiceInfo) || null,
    temporaryLivingInfo: toPascalCaseKeys(flatRecord.temporaryLivingInfo) || null,
    relocationAllowance: toPascalCaseKeys(flatRecord.relocationAllowance) || null,
    homesaleProcess: toPascalCaseKeys(flatRecord.homesaleProcess) || null,
    hhgBenefitsTimeline: toPascalCaseKeys(flatRecord.hhgBenefitsTimeline) || null,
    spousalAssistance: toPascalCaseKeys(flatRecord.spousalAssistance) || null,
    houseHuntingInfo: toPascalCaseKeys(flatRecord.houseHunting) || null,
    finalMoveInfo: toPascalCaseKeys(flatRecord.finalMove) || null,
    candidateInternInfo: toPascalCaseKeys(flatRecord.candidateInternInfo) || null,
    globalServiceInfo: toPascalCaseKeys(flatRecord.globalServiceInfo) || null,
  };
};